import { CheckCircle2, Circle, StickyNote, X } from "lucide-react";
import { chapters } from "../../data/chapters";
import { ScrollArea } from "../ui/scroll-area";

export default function ProgressModal({ data, onClose }) {
  const { user: targetUser, progress } = data;
  const completedSteps = progress?.completed_steps || [];
  const notes = progress?.notes || {};
  const completedAt = progress?.completed_at;

  const chapterChecklistStart = chapters.map((_, i) =>
    chapters.slice(0, i).reduce((sum, c) => sum + c.checklistItems.length, 0)
  );

  const totalChecklist = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
  const progressPercent = totalChecklist > 0 ? Math.round((completedSteps.length / totalChecklist) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        data-testid="progress-modal"
        className="w-full max-w-lg rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[6px_6px_0px_var(--c-shadow)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-[var(--c-border)] p-5">
          <div>
            <h2 className="font-heading text-lg font-bold text-[var(--c-text)]">{targetUser.name || targetUser.email}</h2>
            <p className="text-sm text-[var(--c-muted)]">{progressPercent}% complete &middot; {completedSteps.length}/{totalChecklist} tasks</p>
            {completedAt && (
              <p data-testid="completion-date" className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-[#A3E6D5] bg-[#A3E6D5]/10 px-2.5 py-0.5 text-xs font-bold text-[var(--c-text)]">
                <CheckCircle2 className="h-3 w-3 text-[#A3E6D5]" />
                Completed {new Date(completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-[var(--c-muted)] hover:text-[var(--c-text)]"><X className="h-5 w-5" /></button>
        </div>
        <ScrollArea className="max-h-[60vh] p-5">
          <div className="space-y-4">
            {chapters.map((ch, i) => {
              const startIdx = chapterChecklistStart[i];
              const chCompleted = ch.checklistItems.filter((_, j) => completedSteps.includes(startIdx + j)).length;
              const chTotal = ch.checklistItems.length;
              const allDone = chCompleted === chTotal;
              const chapterNote = notes[String(ch.id)];

              return (
                <div key={ch.id} className="rounded-lg border-2 border-[var(--c-border-light)] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`flex h-6 w-6 items-center justify-center rounded text-xs font-bold ${allDone ? "bg-[#A3E6D5] text-[#1A1918]" : "bg-[var(--c-hover-bg)] text-[var(--c-muted)]"}`}>
                        {allDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : ch.id}
                      </div>
                      <span className="text-sm font-semibold text-[var(--c-text)]">{ch.title}</span>
                    </div>
                    <span className="text-xs font-bold text-[var(--c-muted)]">{chCompleted}/{chTotal}</span>
                  </div>
                  {/* Checklist items */}
                  <div className="mt-2 space-y-1 pl-8">
                    {ch.checklistItems.map((item, ci) => {
                      const checked = completedSteps.includes(startIdx + ci);
                      return (
                        <div key={`${ch.id}-item-${ci}`} className="flex items-start gap-2">
                          {checked ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" /> : <Circle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--c-border-light)]" />}
                          <span className={`text-xs ${checked ? "text-[var(--c-muted)] line-through" : "text-[var(--c-text)]"}`}>{item}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Note */}
                  {chapterNote && (
                    <div className="mt-2 ml-8 flex items-start gap-1.5 rounded border border-[var(--c-border-light)] bg-[var(--c-hover-bg)] p-2">
                      <StickyNote className="mt-0.5 h-3 w-3 flex-shrink-0 text-[#1E3A8A]" />
                      <p className="text-xs text-[var(--c-muted)]">{chapterNote}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
